import { ManagerDashboardContent, ManagerDashboardHeader } from './subcomponents';
import { UnlockCertificationModal } from '@/components/Modals';
import { useManagerDashboardFeature } from '@/features/manager-dashboard';
import React from 'react';

/**
 * ManagerDashboard Component
 *
 * This component serves as the main dashboard for managers to monitor their team's readiness.
 * It displays a list of team members along with their certification statuses, practice certifications,
 * shared role-play levels, and recent activity. Managers can filter the team members based on their
 * certification status and view detailed profiles for each member.
 */
export const ManagerDashboard: React.FC = () => {
  // ================================
  // Feature Hook
  // ================================
  const {
    activeFilter,
    currentUser,
    filteredResultsText,
    filteredUsers,
    handleFilterClear,
    handleFilterSelect,
    handleModalUnlockCancel,
    handleModalUnlockConfirm,
    handleProductChange,
    handleSort,
    isLoading,
    managedUsers,
    managerActiveProduct,
    productOptions,
    selectedUserForUnlock,
    showFilterResultsText,
    sortConfig,
    tableColumns,
    unlockModalError,
    unlockModalOpen,
    usersCertified,
    usersNeedingAttention,
    usersPassedPractice,
  } = useManagerDashboardFeature();

  // ================================
  // Render
  // ================================
  return (
    <>
      <ManagerDashboardHeader currentUser={currentUser} isLoading={isLoading} />

      <ManagerDashboardContent
        activeFilter={activeFilter}
        filteredResultsText={filteredResultsText}
        filteredUsers={filteredUsers}
        handleFilterClear={handleFilterClear}
        handleFilterSelect={handleFilterSelect}
        handleProductChange={handleProductChange}
        handleSort={handleSort}
        isLoading={isLoading}
        managedUsers={managedUsers}
        managerActiveProduct={managerActiveProduct}
        productOptions={productOptions}
        showFilterResultsText={showFilterResultsText}
        sortConfig={sortConfig}
        tableColumns={tableColumns}
        usersCertified={usersCertified}
        usersNeedingAttention={usersNeedingAttention}
        usersPassedPractice={usersPassedPractice}
      />

      <UnlockCertificationModal
        activeProduct={managerActiveProduct}
        errorMessage={unlockModalError}
        isOpen={unlockModalOpen}
        onCancel={handleModalUnlockCancel}
        onConfirm={handleModalUnlockConfirm}
        userIdToUnlock={selectedUserForUnlock?.userId}
      />
    </>
  );
};

ManagerDashboard.displayName = 'ManagerDashboard';

export default ManagerDashboard;
